import { EmptyView, Item } from '@components/cart'
import { ErrorView } from '@components/cart/error-view'
import {
  AtButton,
  AtButtonVariant,
  getRichTextNode,
  MlHeading,
  OrModal,
  TextAlignment,
} from '@curran-catalog/curran-atomic-library'
import { Transition } from '@headlessui/react'
import { useAuth } from '@hooks/use-auth'
import { useCart } from '@hooks/use-cart'
import { sendSendCartRequest } from '@services/cart'
import { internationalGuestUser, internationalLogInUser, notLoggedInModalDocument } from '@utils/rich-text-templates'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { CartItem } from 'types'
import OrderSummaryDefault from './order-summary-default'
import OrderSummarySynsisal from './order-summary-synsisal'
import { detectSiteDomain } from '@utils/detect-site-domain'
import { sendCheckoutAnalyticsEvent } from '@utils/analytics/events/checkout/send-event'
import { CartEventTypes, CheckoutEventTypes } from '../../types/analytics/analytics-data'
import { sendCartAnalyticsEvent } from '@utils/analytics/events/cart/send-event'
import { useSessionStorage } from '@hooks/use-session-storage'

const { furnitureSite, synsisalSite, flooringSite, sisalcarpetSite } = detectSiteDomain()

export const TmCart = () => {
  const [showInternationalModal, setShowInternationalModal] = useState(false)
  const [showNotLoggedInModal, setShowNotLoggedInModal] = useState(false)
  const [showEstimateTaxInput, setShowEstimateTaxInput] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [previousListRoute] = useSessionStorage('x-previousListTitleRoute', '')

  const { applyPromo, cart, error, estimateTax, isLoading, promoError, removePromo } = useCart()
  const { user } = useAuth({})
  const router = useRouter()

  useEffect(() => {
    if ((synsisalSite || flooringSite || sisalcarpetSite) && !isLoading && cart) {
      sendCartAnalyticsEvent(cart.items, CartEventTypes.VIEW_CART, undefined, undefined, previousListRoute)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleEstimateTaxZipSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const zip = e.currentTarget.zip.value
    estimateTax({ zip })
    setShowEstimateTaxInput(false)
  }

  const handlePromoSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const promoCode = e.currentTarget.promoCode.value
    applyPromo({ code: promoCode })
  }

  const handleSendCart = async () => {
    setShowInternationalModal(false)
    await sendSendCartRequest().then(() => {
      toast.success('Cart sent successfully')
    })
  }

  const hasProdRush = (item: CartItem) => {
    if (cart?.items.filter((item1) => item1.sku == 'OT-HOTRUSH' && item.id + 1 == item1.id).length != 0) {
      return true
    } else {
      return false
    }
  }

  const handleCheckoutClick = () => {
    setIsSubmitting(true)
    location.assign('/checkout')
    if (cart) {
      sendCheckoutAnalyticsEvent(
        cart,
        CheckoutEventTypes.BEGIN_CHECKOUT,
        undefined,
        undefined,
        undefined,
        undefined,
        previousListRoute,
      )
    }
  }
  return (
    <>
      <Transition
        show={isLoading || error !== undefined || cart?.count === 0}
        enter="transition-opacity duration-75 delay-150"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-250 absolute inset-x-0"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <>{error ? <ErrorView /> : <EmptyView isLoading={isLoading} />}</>
      </Transition>
      <Transition
        show={!isLoading && !error && cart?.count !== 0}
        enter="transition-opacity duration-75 delay-150"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-150"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <MlHeading title={getRichTextNode('Shopping Cart', 'heading-1')} textAlignment={TextAlignment.LEFT} />
        <div className="mt-12 lg:grid lg:grid-cols-12 lg:items-start lg:gap-x-12 xl:gap-x-16">
          <section aria-labelledby="cart-heading" className="lg:col-span-7">
            <h2 id="cart-heading" className="sr-only">
              Items in your shopping cart
            </h2>
            <ul role="list" className="divide-y divide-outlined-gray border-t border-b border-outlined-gray">
              {cart?.items.map((item: CartItem, itemIdx: number) => {
                return <Item key={item.id} item={item} itemIdx={itemIdx} isEditable isProdRush={hasProdRush(item)} />
              })}
            </ul>
          </section>

          {synsisalSite ? (
            <OrderSummarySynsisal cart={cart} handleCheckoutClick={handleCheckoutClick} isSubmitting={isSubmitting} />
          ) : (
            <OrderSummaryDefault
              promoError={promoError}
              showEstimateTaxInput={showEstimateTaxInput}
              isSubmitting={isSubmitting}
              removePromo={removePromo}
              handlePromoSubmit={handlePromoSubmit}
              handleCheckoutClick={handleCheckoutClick}
              setShowEstimateTaxInput={setShowEstimateTaxInput}
              handleEstimateTaxZipSubmit={handleEstimateTaxZipSubmit}
              cart={cart}
            />
          )}

          {!user && (
            <OrModal
              title="You're not logged in"
              description={{
                text: notLoggedInModalDocument,
              }}
              icon="user"
              isOpen={showNotLoggedInModal}
              rightButton={{ label: 'Create account', onClick: () => router.push('/register?intended=cart') }}
              leftButton={{
                label: 'Continue as guest',
                onClick: () => location.assign('/checkout'),
              }}
              handleCloseModal={() => {
                setShowNotLoggedInModal(false)
                setIsSubmitting(false)
              }}
            />
          )}
          <OrModal
            title="Shipping outside of the USA?"
            description={{
              text: furnitureSite ? internationalLogInUser : internationalGuestUser,
            }}
            icon="globe"
            isOpen={showInternationalModal}
            leftButton={{ label: 'Dismiss', onClick: () => setShowInternationalModal(false) }}
            rightButton={{
              label: user ? 'Send Cart' : 'Login or Create Account',
              onClick: user ? () => handleSendCart() : () => router.push('/login'),
            }}
            handleCloseModal={() => {
              setShowInternationalModal(false)
            }}
          />
          {!synsisalSite && !cart?.hasBeenSent && (
            <div className="lg:col-span-3 mt-6">
              <AtButton
                className="w-full"
                variant={AtButtonVariant.TERTIARY}
                onClick={() => setShowInternationalModal(true)}
                label="International"
                icon={{ type: 'globe' }}
              />
            </div>
          )}
        </div>
      </Transition>
    </>
  )
}
